import algoliasearch, { SearchClient } from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import { connectSortBy, connectHits } from 'instantsearch.js/es/connectors';
import { history } from 'instantsearch.js/es/lib/routers';
import {
  stats,
  clearRefinements,
  rangeSlider,
  pagination,
  configure,
} from 'instantsearch.js/es/widgets';
import $ from 'jquery';

// Frontend imports can't use app-absolute URLs, so we need to use relative paths
import getFilterObjects from '../../../../app/Libraries/Algolia/frontend-filter-objects';

import {
  formatAsMoney,
  extractID,
  getSizedShopifyImageUrl,
} from '../../modules/utils';

const searchClient = algoliasearch(
  'NOZ1QDJQX7',
  '7a90c3feb1020c684066202bacc30669'
);

const PRODUCTS_INDEX = 'shopify_products';
const PRODUCTS_INDEX_ASC = 'shopify_products_asc';
const PRODUCTS_INDEX_PRICE_ASC = 'shopify_products_price_asc';
const PRODUCTS_INDEX_PRICE_DESC = 'shopify_products_price_desc';
const PRODUCTS_INDEX_ALPHA_ASC = 'shopify_products_alphabetical_asc';
const PRODUCTS_INDEX_ALPHA_DESC = 'shopify_products_alphabetical_desc';

// const DEFAULT_INDEX = PRODUCTS_INDEX_PRICE_ASC;
const DEFAULT_INDEX = PRODUCTS_INDEX;

// This allows us to cancel a hits render if a new one is triggered before the last one finishes
let latestCallTimestamp = new Date().getTime();

/**
 * Make async calls and render the hits once they've been loaded
 * @param hits The hits from the Algolia renderHits function
 * @param container The container to render the hits into
 * @param timestamp The timestamp for when this call was made. Used for skipping render if out of date.
 */
const loadHitsAsync = async (hits: any[], container: HTMLElement, timestamp: number) => {
  // console.debug(`🚀 ~ loadHitsAsync (@${timestamp}) start`);
  const innerHtml = await Promise.all(
    hits.map(async (hit) => {
      let classes: string = '';
      let images: any[] = [];

      let namedTags = hit.named_tags;

      images.push({
        // Default image
        url: hit.image
          ? getSizedShopifyImageUrl(hit.image, 768)
          : '/img/placeholder.png',
        srcSet: hit.image
          ? `${getSizedShopifyImageUrl(hit.image, 768)} 767w, ${getSizedShopifyImageUrl(hit.image, 1200)} 1200w`
          : '/img/placeholder.png',
        class: 'primary',
      });

      // Make a call to get the images
      const productImages = await fetch(
        `/api/shop/product-images/${hit.id}`
      ).then((r) => r.json());

      if (productImages.length > 1) {
        images.push({
          url: productImages[1].thumbnail,
          srcSet: `${productImages[1].thumbnail} 767w, ${productImages[1].url} 1200w`,
          class: 'secondary',
        });

        classes += 'product-card--multi-image';

        if (namedTags.hover && namedTags.hover === 'lifestyle') {
          classes += ' product-card--lifestyle-image';
        }
      }

      let imageHTML = images
        .map((image) => {
          return `<img class="product-card__img product-card__img--${image.class} lazyload"
          data-src="${image.url}" data-srcset="${image.srcSet}" />`;
        })
        .join('');

      let imageButtons = '';

      if (images.length > 1) {
        imageButtons =
          `<button class="product-card__img-button product-card__img-button--prev js-prev-img" aria-label="Previous Image" disabled></button>
          <button class="product-card__img-button product-card__img-button--next js-next-img" aria-label="Next Image"></button>`;
      }

      let badgeHTML = '';
      let buttonHTML = '';

      let tags = hit.tags;

      let available = namedTags.available
        ? hit.inventory_available
          ? true
          : false
        : false;

      if (
        hit.option_names.length > 1 ||
        hit.option_names.some((e) => e.includes('size')) ||
        tags?.includes('resizeable')
      ) {
        available = false;
      }

      if (namedTags.newArrival) {
        badgeHTML =
          '<span class="product-card__badge product-card__badge--new">New</span>';
      }

      if (hit.option_names.length > 1) {
        buttonHTML = '';
      } else if (available) {
        buttonHTML = `<button class="bag-btn product-card__add-to-cart js-add-to-cart" data-id="gid://shopify/ProductVariant/${hit.objectID}">
                <span class="sr-only">Add to Cart</span>
              </button>`;
      } else {
        buttonHTML = `<button class="bag-btn product-card__add-to-cart js-add-to-cart hidden" disabled data-id="gid://shopify/ProductVariant/${hit.objectID}">
                <span class="sr-only">Add to Cart</span>
              </button>`;
      }

      // variants should only show for colour options, not size etc.
      // We're showing 3, and if there are more than that the plus button shows
      let variantsHTML = '';

      // Check if there are colour or material variants for this item
      if (
        hit.option_names.includes('color') ||
        hit.option_names.includes('colour') ||
        hit.option_names.includes('material')
      ) {
        // Make a call to get the variants
        const variantColours = await fetch(
          `/api/shop/variant-colours/${hit.id}`
        ).then((r) => r.json());

        if (variantColours.length > 1) {
          let html = variantColours
            .map((variant) => {
              let checked =
                Object.values(hit.options).indexOf(variant.value) === 0
                  ? 'checked'
                  : '';

              // we don't want to actually disable, but simulate instead
              let disabled = variant.inStock ? '' : 'disabled';
              // Don't set imageset attr if no variant image
              const imageSet = variant.image
                ? `${getSizedShopifyImageUrl(variant.image, 768)} 767w, ${getSizedShopifyImageUrl(variant.image, 1200)} 1200w`
                : '';
              // let disabled = '';
              return `<label class="variant-selectors__option variant-selectors__option--color variant-selectors__option--${
                variant.classes
              } js-mini-variant-selector">
              <input class="variant-selectors__input ${disabled}" type="radio" name="${
                hit.handle
              }-option-${variant.name}" value="${variant.value}" data-option="${
                variant.name
              }" data-product="${hit.handle}" data-imageset="${
                imageSet
              }" data-price="${formatAsMoney(variant.price)}" data-compare-at-price="${
                variant.compareAtPrice ? formatAsMoney(variant.compareAtPrice) : ''
              }" data-variant="${
                variant.id
              }" ${checked} />
              <span class="variant-selectors__option-btn" title="${
                variant.value
              }"></span>
              <span class="variant-selectors__option-strikethrough"></span>
              <span class="variant-selectors__option-underline"></span>
            </label>
            `;
            })
            .slice(0, 4)
            .join('');

          if (variantColours.length > 3) {
            html += '<span class="variant-selectors__additional">+</span>';
          }

          variantsHTML = `<div role="group" aria-labelledby="option-label_colour" class="variant-selectors variant-selectors--mini">
          <span class="sr-only" id="option-label__colour">Select a Colour</span>
          <div class="variant-selectors__buttons">
            ${html}
          </div>
        </div>`;
        }
      }

      // add to cart button needs to be conditional, should not show if its not
      // availble online, out of stock, or if there are more than just colour
      // selector options (ie if they need to choose a size or something, should not show)
      /****** If you make ANY EDITS to this you need to also update the ProductCard component file as well ******/

      let priceMarkup = '';
      // If price hidden
      if (hit.tags?.includes('hide-price')) {
        priceMarkup = '<p class="product-card__price product-card__price--hidden">Price upon request</p>';
      } else if (hit.compare_at_price && hit.price !== hit.compare_at_price) {
        // If has a different compare-at price, show it
        priceMarkup = `<p class="product-card__price js-product-price">
          <strike>$${hit.compare_at_price.toLocaleString('en-US')}</strike><span>$${hit.price.toLocaleString('en-US')}</span>
        </p>`;
      } else {
        // Default just show price
        priceMarkup = `<p class="product-card__price js-product-price">
          $${hit.price.toLocaleString('en-US')}
        </p>`;
      }

      return `
      <li class="collection__grid-item">
        <div class="product-card ${classes} js-product" data-product="${
        hit.handle
      }">
            <a href="/products/${hit.handle}?v=${
        hit.objectID
      }" class="product-card__images" tabindex="-1"
              >${imageHTML}</a
            >

            ${badgeHTML}

            <div class="product-card__details">
              <h3 class="product-card__title"><a href="/products/${
                hit.handle
              }">${hit.title}</a></h3>
              ${priceMarkup}
            </div>

            ${imageButtons}

            <div class="product-card__btns">
              ${buttonHTML}
              ${variantsHTML}
            </div>

          </div>
        </li>
        `;
    })
  );

  // If this call's timestamp is not out of date, update the UI
  if (latestCallTimestamp <= timestamp) {
    // console.debug(`🚀 ~ loadHitsAsync (@${timestamp}) updating`);
    container.innerHTML = `
      <ol class="collection__grid">
        ${innerHtml.join('')}
      </ol>`;
  // } else {
  //   console.debug(`🚀 ~ loadHitsAsync (@${timestamp}) cancelled and skipping`);
  }
};

/**
 * The renderHits function for the custom hits widget.
 * It sets the initial state to show a loading spinner,
 * and calls the loadHitsAsync function to render the hits.
 * It will abort the render if showing the "no hits" message.
 */
const renderHits = (renderOptions, isFirstRender: boolean) => {
  const { hits, widgetParams } = renderOptions;
  // console.debug('🚀 ~ renderHits ~ hits:', hits);

  // When renderHits is called, we update the latest timestamp
  latestCallTimestamp = new Date().getTime();

  if (isFirstRender) {
    // for some reason I can only get this to work properly if its run on first render, using jquery...
    // - otherwise click event fires 3 times?
    bindHitEventListeners();
    return;
  }

  if (!hits.length) {
    widgetParams.container.innerHTML = `
      <ol class="collection__grid collection__grid--empty">
        <li class="collection__grid-item collection__grid-item--empty">
          <p class="collection__empty-msg">No results found matching your selection.</p>
        </li>
      </ol>`;
    // If we hit here, we need to bump the timestamp to stop any in-progress `loadHits()` calls
    // from replacing the content
    // console.debug('🚀 ~ renderHits ~ cancelling)');
    latestCallTimestamp = new Date().getTime();
    return;
  }

  // Show loading spinner
  widgetParams.container.innerHTML = `
    <ol class="collection__grid collection__grid--loading">
      <li class="collection__grid-item collection__grid-item--loading">
        <div class="pagespinner pagespinner--small spin"></div>
      </li>
    </ol>`;

  // NOTE: This is async but we just call it so it updates the UI when ready
  // console.debug(`🚀 ~ renderHits ~ calling loadHitsAsync (@${latestCallTimestamp})`);
  loadHitsAsync(hits, widgetParams.container, latestCallTimestamp);

  function bindHitEventListeners() {
    // moved add to cart to global-cart.js file

    // not sure how to do this with vanilla js since elements are dynamically created?

    $('.collection').on('mouseover', '.js-product', (e) => {
      if (window.matchMedia('(pointer: fine)').matches) {
        const $card = e.currentTarget;
        $card.classList.add('hover');
      }
    });

    $('.collection').on('mouseout', '.js-product', (e) => {
      if (window.matchMedia('(pointer: fine)').matches) {
        const $card = e.currentTarget;
        $card.classList.remove('hover');
      }
    });

    $('.collection').on(
      'focusin',
      '.js-product input, .js-product a, .js-product button',
      (e) => {
        if (window.matchMedia('(pointer: fine)').matches) {
          const $card = e.currentTarget.closest('.js-product');

          if (!$card.classList.contains('hover')) {
            document
              .querySelector('.js-product.hover')
              ?.classList.remove('hover');
            $card.classList.add('hover');
          }
        }
      }
    );

    $('.collection').on(
      'blur',
      '.js-product input, .js-product a, .js-product button',
      (e) => {
        if (window.matchMedia('(pointer: fine)').matches) {
          const $card = e.currentTarget.closest('.js-product');
          const allInputs = $card.querySelectorAll('input,a,button');

          // if we're losing focus on the last element, aka tabbing into the next div
          if (e.currentTarget == allInputs[allInputs.length - 1]) {
            $card.classList.remove('hover');
          }

          // if we're losing focus on the first element and the new target isn't in the same div,
          // we're tabbing backwards
          if (!$card.contains(e.relatedTarget)) {
            $card.classList.remove('hover');
          }
        }
      }
    );

    $('.collection').on('click', '.js-prev-img', (e) => {
      e.preventDefault();
      const card = e.currentTarget.closest('.js-product');
      mobileFirstImage(card);
    });

    $('.collection').on('click', '.js-next-img', (e) => {
      e.preventDefault();
      const card = e.currentTarget.closest('.js-product');
      mobileSecondaryImage(card);
    });

    function mobileFirstImage(card) {
      card.classList.remove('hover');
      card.querySelector('.js-prev-img').disabled = true;
      card.querySelector('.js-next-img').disabled = false;
    }

    function mobileSecondaryImage(card) {
      card.classList.add('hover');
      card.querySelector('.js-prev-img').disabled = false;
      card.querySelector('.js-next-img').disabled = true;
    }

    // swiping on mobile

    let touchStartX = 0;
    let touchEndX = 0;
    const swipeThreshold = 50; // Adjust this value as needed

    $('.collection').on(
      'touchstart',
      '.js-product:not(.disable-hover)',
      function(e) {
        touchStartX = e?.originalEvent?.touches[0].clientX ?? 0;
      }
    );

    $('.collection').on(
      'touchend',
      '.js-product:not(.disable-hover)',
      function(e) {
        touchEndX = e?.originalEvent?.changedTouches[0].clientX ?? 0;

        const deltaX = touchEndX - touchStartX;
        const card = e.currentTarget.closest('.js-product');

        if (deltaX > swipeThreshold) {
          // Right swipe
          mobileFirstImage(card);
        } else if (deltaX < -swipeThreshold) {
          // Left swipe
          mobileSecondaryImage(card);
        }
      }
    );

    $('.collection, .search-results__product-grid, .product-list').on(
      'click',
      '.js-mini-variant-selector',
      (e) => {
        let input = e.target;

        let id = input.dataset.variant;
        let imageSet = input.dataset.imageset;
        let price = input.dataset.price;
        let compareAtPrice = input.dataset.compareAtPrice;
        let handle = input.dataset.product;
        let available = input.classList.contains('disabled') ? false : true;

        let product = document.querySelector(
          `.js-product[data-product="${handle}"]`
        );

        if (product) {
          let addToCart = product.querySelector(
            '.js-add-to-cart'
          ) as HTMLInputElement | null;

          let links = product.querySelectorAll('a');

          links?.forEach((link) => {
            let url = new URL(link.href);
            let params = url.searchParams;

            params.set('v', extractID(id));

            url.search = params.toString();

            link.href = url.toString();
          });

          if (addToCart) {
            // update id on cart button
            addToCart.dataset.id = id;

            if (available) {
              addToCart.classList.remove('hidden');
              addToCart.disabled = false;
            } else {
              addToCart.classList.add('hidden');
              addToCart.disabled = true;
            }
          }

          if (imageSet) {
            // update image
            const imgEl = product.querySelector('.product-card__img--primary') as HTMLImageElement;
            imgEl.srcset = imageSet;
            product.classList.add('disable-hover');
          }
          const priceEl = product.querySelector('.js-product-price');
          if (priceEl) {
            let priceMarkup = '';
            if (compareAtPrice && price !== compareAtPrice) {
              priceMarkup += `<strike>${compareAtPrice}</strike>`;
            }
            priceMarkup += `<span>${price}</span>`;
            priceEl.innerHTML = priceMarkup;
          }
        }
      }
    );

    $('.collection').on('click', '.js-product', (e) => {
      if (window.fbq) {
        window.fbq('trackCustom', 'SelectContent', {
          product: e.currentTarget.getAttribute('data-product'),
        });
      }
    });
  }
};

const customHits = connectHits(renderHits);

// Create the render function
const renderSortBy = (renderOptions, isFirstRender) => {
  const {
    options,
    currentRefinement,
    refine,
    widgetParams,
    // `canRefine` is only available from v4.45.0
    // Use `hasNoResults` in earlier minor versions.
  } = renderOptions;

  const selectedOption = options.find((option) => {
    return option.value === currentRefinement;
  });

  //TODO: not sure what firstrender implies, but all event listeners have to be in first
  if (isFirstRender) {
    widgetParams.container.classList.add('sortBy');
    // const header = document.createElement('div');
    widgetParams.container.innerHTML =
      '<span class="sortBy__label">Sort by:</span>';

    const toggle = document.createElement('button');
    toggle.classList.add('sortBy__toggle', 'js-sortBy');

    const toggleText = `${selectedOption.label}`;

    toggle.innerHTML = toggleText;

    widgetParams.container.appendChild(toggle);

    const list = document.createElement('ul');
    list.setAttribute('aria-hidden', 'true');
    list.classList.add('sortBy__list', 'js-sortBy-list');

    widgetParams.container.appendChild(list);

    options.forEach((option) => {
      const button = document.createElement('button');
      button.dataset.value = option.value;
      button.classList.add('sortBy__button');

      button.innerHTML = `<span>${option.label}</span>`;

      if (option.value === currentRefinement) {
        button.classList.add('sortBy__button--selected');
      }

      button.addEventListener('click', (event) => {
        event.preventDefault();
        let oldBtn = widgetParams.container.querySelector(
          '.sortBy__button--selected'
        );

        oldBtn.classList.remove('sortBy__button--selected');

        let btn = event.currentTarget as HTMLElement;

        btn.classList.add('sortBy__button--selected');

        let toggles = document.querySelectorAll('.sortBy__toggle');

        toggles.forEach((toggle) => {
          toggle.innerHTML = btn.innerHTML;
        });

        refine(btn!.dataset.value);

        // close dropdown
        toggle.classList.remove('sortBy__toggle--open');
        list.setAttribute('aria-hidden', 'true');
      });

      const li = document.createElement('li');
      li.className = 'sortBy__list-item';

      li.append(button);

      list.append(li);
    });

    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      if (toggle.classList.contains('sortBy__toggle--open')) {
        toggle.classList.remove('sortBy__toggle--open');
        list.setAttribute('aria-hidden', 'true');
      } else {
        toggle.classList.add('sortBy__toggle--open');
        list.removeAttribute('aria-hidden');
      }
    });
  }
};

const customSortBy = connectSortBy(renderSortBy);

// @ts-ignore
function init(searchClient: SearchClient) {
  const mainElem = document.querySelector('main[data-collection]') as HTMLElement;
  const collectionHandle = mainElem.dataset.collection ?? 'default';
  const collectionClass = [...mainElem.classList].filter((c) => c.includes('collection-page--'))[0];
  // console.debug({ collectionClass });
  const additionalFilters = getFilterObjects(collectionHandle, collectionClass);

  const search = instantsearch({
    indexName: DEFAULT_INDEX,
    routing: {
      router: history(),
      stateMapping: {
        // @ts-ignore
        stateToRoute(uiState) {
          console.debug({ stateToRoute: uiState });

          const indexUiState = uiState[DEFAULT_INDEX];
          // This is the format used below in routeToState
          let refinements = {};

          additionalFilters.forEach((filter: any) => {
            refinements[filter.id] = indexUiState[filter.widgetType]?.[filter.attribute];
          });

          // Price widget is declared separately from the rest of the filters
          if (indexUiState['range']?.price) {
            refinements['price'] = indexUiState['range'].price;
          }

          // console.debug('stateToRoute', additionalFilters, refinements);

          return {
            ...refinements,
            page: indexUiState.page,
          };
        },
        // @ts-ignore
        routeToState(routeState) {
          console.debug({ routeToState: routeState });

          let refinements = {};

          additionalFilters.forEach((filter: any) => {
            if (!refinements[filter.widgetType]) {
              refinements[filter.widgetType] = {};
            }
            refinements[filter.widgetType][filter.attribute] = routeState[filter.id];
          });

          // Price widget is declared separately from the rest of the filters
          if (routeState.price) {
            refinements['range'] = {
              ...refinements['range'],
              price: routeState.price,
            };
          }

          // console.debug('routeToState', additionalFilters, refinements);

          return {
            [DEFAULT_INDEX]: {
              // These basically have a key for each widget type, with keys for each attribute
              // e.g. { refinementList: { 'named_tags.collection': ['collection'] } }
              page: routeState.page,
              ...refinements,
            },
          };
        },
      },
    },
    searchClient,
  });

  // Make a json file for collections
  /**
   * Include these widgets:
   * sort by,
   * price range,
   * categories (to be developed),
   * (all refinementlists, including purchaseOptions), - this is the dynamic ones enabled per-collection
   * (toggleRefinements),
   * new arrivals
   */

  document.addEventListener('DOMContentLoaded', () => {
    // Trigger Facebook Pixel tracking for the collection page
    if (window.fbq) {
      window.fbq('trackCustom', 'ViewItemList', { collection: collectionHandle });
    }

    const widgets: any = [
      customHits({
        // @ts-ignore (apparently the types haven't been added/updated for custom connectors yet?)
        container: document.querySelector('#hits'),
      }),
      stats({
        container: '#stats',
        templates: {
          text(data: any, { html }) {
            const { hitsPerPage, nbHits, nbPages, page, hasNoResults } = data;

            const nbPage = page + 1;

            if (hasNoResults) {
              return html`<span>No results found</span>.`;
            } else {
              const startNum = page * hitsPerPage + 1;

              const endNum = nbPage < nbPages ? nbPage * hitsPerPage : nbHits;

              return html`<span
                >Showing ${startNum}-${endNum} of ${nbHits}</span
              >`;
            }
          },
        },
      }),
      // The "sort by" on the main page
      customSortBy({
        // @ts-ignore (apparently the types haven't been added/updated for custom connectors yet?)
        container: document.querySelector('#sortBy'),
        items: [
          { label: 'Price low to high', value: PRODUCTS_INDEX_PRICE_ASC },
          { label: 'Price high to low', value: PRODUCTS_INDEX_PRICE_DESC },
          { label: 'New to old (Newest)', value: PRODUCTS_INDEX },
          { label: 'Old to new (Oldest)', value: PRODUCTS_INDEX_ASC },
          {
            label: 'Product name A - Z',
            value: PRODUCTS_INDEX_ALPHA_ASC,
          },
          {
            label: 'Product name Z - A',
            value: PRODUCTS_INDEX_ALPHA_DESC,
          },
        ],
        // Optional parameters
      }),
      // The "sort by" in the filters sidebar
      customSortBy({
        // @ts-ignore (apparently the types haven't been added/updated for custom connectors yet?)
        container: document.querySelector('#filters-sortBy'),
        items: [
          { label: 'Price low to high', value: PRODUCTS_INDEX_PRICE_ASC },
          { label: 'Price high to low', value: PRODUCTS_INDEX_PRICE_DESC },
          { label: 'New to old (Newest)', value: PRODUCTS_INDEX },
          { label: 'Old to new (Oldest)', value: PRODUCTS_INDEX_ASC },
          {
            label: 'Product name A - Z',
            value: PRODUCTS_INDEX_ALPHA_ASC,
          },
          {
            label: 'Product name Z - A',
            value: PRODUCTS_INDEX_ALPHA_DESC,
          },
        ],
        // Optional parameters
      }),
      // Price range slider
      rangeSlider({
        container: '#priceSlider',
        attribute: 'price',
        min: 0,
        pips: false,
        step: 100,
        tooltips: {
          format: (rawValue) => {
            return formatAsMoney(rawValue);
          },
        },
        cssClasses: {
          root: 'range-slider',
        },
      }),
      pagination({
        container: '#pagination',
        templates: {
          first: 'First',
          last: 'Last',
        },
        padding: 2,
        scrollTo: '.collection',
        cssClasses: {
          root: 'pagination',
          noRefinementRoot: 'pagination pagination--empty',
          list: 'pagination__list',
          item: 'pagination__item',
          firstPageItem: 'pagination__item--first',
          lastPageItem: 'pagination__item--last',
          previousPageItem: 'pagination__item--prev pagination__item--arrow',
          nextPageItem: 'pagination__item--next pagination__item--arrow',
          pageItem: 'pagination__item--page',
          selectedItem: 'pagination__item--selected',
          disabledItem: 'pagination__item--disabled',
          link: 'pagination__link',
        },
      }),
      clearRefinements({
        container: '#clear-filters',
        templates: {
          // @ts-ignore
          resetLabel({ hasRefinements }, { html }) {
            return html`<span>Reset Filters</span>`;
          },
        },
      }),
    ];

    // Add the collection to the configure widget if we have a collection page
    if (collectionHandle) {
      widgets.push(
        configure({
          filters: `collections:${collectionHandle}`,
          maxValuesPerFacet: 50,
        })
      );
    }

    // Add any additional filters for the collection
    widgets.push(
      ...additionalFilters.map((filter) => {
        console.debug(filter);
        return filter.generateWidget();
      }),
    );

    search.addWidgets(widgets);
    search.start();
  });
}

init(searchClient);
